/**
 * GridHoverCard — the card that follows the pointer over a grid block. One component for the Counter and
 * Officer tabs so the same block reads the same on both: the counter and the time as pills tinted in the
 * block's own colour, the officer's name and deployment beneath, then any note and the edit hint.
 * Light card, dark text — the grids are light, and white-on-black text was hard to read over them.
 */
import { forwardRef, type CSSProperties } from 'react';
import { getOfficerColor } from '@/config/modeConfig';
import type { HoverInfo, OfficerMetadata } from '@/types/api';

const INK = '#0f172a';
const MUTED = '#64748b';

function Pill({ color, children, strong }: { color: string; children: React.ReactNode; strong?: boolean }) {
  return (
    <span
      style={{
        background: color,
        color: INK,
        border: '1px solid rgba(0,0,0,0.18)',
        borderRadius: 10,
        padding: '1px 8px',
        fontSize: 12,
        fontWeight: strong ? 700 : 600,
        whiteSpace: 'nowrap',
        lineHeight: '18px',
      }}
    >
      {children}
    </span>
  );
}

/** "1630-1730" → ["1630", "1730"]; anything else comes back whole. */
function Time({ range, color }: { range: string; color: string }) {
  return <Pill color={`${color}99`}>{range.replace('-', '–')}</Pill>;
}

function Person({ id, meta }: { id: string; meta?: OfficerMetadata }) {
  return (
    <div>
      <div style={{ fontWeight: 600, color: INK, overflowWrap: 'anywhere' }}>{meta?.name || id}</div>
      {meta?.deployment && <div style={{ fontSize: 12, color: MUTED }}>{meta.deployment}</div>}
    </div>
  );
}

export interface GridHoverCardProps {
  info: HoverInfo;
  x: number;
  y: number;
  /** The counter tab can drag a block up/down to move it; the officer tab cannot. */
  canDrag?: boolean;
  /** Label of the hovered block: the counter. On the officer tab that is the block's text, on the
   *  counter tab it is the row. */
  counterLabel: string;
}

export const GridHoverCard = forwardRef<HTMLDivElement, GridHoverCardProps>(function GridHoverCard(
  { info, x, y, canDrag, counterLabel }, ref,
) {
  const box: CSSProperties = {
    position: 'fixed', top: `${y + 16}px`, left: `${x + 16}px`,
    background: '#ffffff', color: INK, padding: '10px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #cbd5e1', boxShadow: '0 6px 20px rgba(15,23,42,0.22)',
    pointerEvents: 'none', zIndex: 9000, maxWidth: 260,
  };
  const noteEl = info.note ? (
    <div style={{ marginTop: 6, fontSize: 12, whiteSpace: 'normal', overflowWrap: 'anywhere', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: '3px 6px' }}>
      📝 {info.note}
    </div>
  ) : null;

  if (info.officers && info.officers.length > 1) {
    return (
      <div ref={ref} style={box}>
        {info.officers.map((o, i) => {
          const color = getOfficerColor(o.id, o.metadata);
          return (
            <div key={o.id} style={{ marginTop: i > 0 ? 8 : 0, paddingTop: i > 0 ? 8 : 0, borderTop: i > 0 ? '1px solid #e2e8f0' : undefined }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                <Pill color={color} strong>{o.counter}</Pill>
                {o.timeRange && <Time range={o.timeRange} color={color} />}
              </div>
              <Person id={o.id} meta={o.metadata} />
            </div>
          );
        })}
      </div>
    );
  }

  if (!info.officer || !info.metadata) {
    return (
      <div ref={ref} style={box}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <Pill color="#e2e8f0" strong>{counterLabel}</Pill>
          <Time range={info.time} color="#e2e8f0" />
        </div>
        {noteEl}
        <div style={{ marginTop: 6, fontSize: 12, color: MUTED }}>No assignment — drag to assign</div>
      </div>
    );
  }

  const color = info.color ?? getOfficerColor(info.officer, info.metadata);
  return (
    <div ref={ref} style={box}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        <Pill color={color} strong>{counterLabel}</Pill>
        <Time range={info.time} color={color} />
      </div>
      <Person id={info.officer} meta={info.metadata} />
      {noteEl}
      <div style={{ marginTop: 6, fontSize: 11, color: MUTED }}>
        {canDrag ? 'Click to edit · drag up/down to move' : 'Click to edit'}
      </div>
    </div>
  );
});
